// @flow

import React, { PureComponent, type Element } from 'react';
import { connect } from 'react-redux';
import FollowersCountText from 'app/common/react/components/FeedsAndFollowing/FollowButton/FollowersCountText';
import i18n from 'app/common/lib/i18n';
import Toast from 'app/common/react/ui/Toast';
import type { Dispatch } from 'redux';
import withShowToast from 'app/common/lib/react/withShowToast';
import { compose } from 'redux';
import { setMostRecentPin } from 'app/common/redux/actions/mostRecentPin';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { Box, Button, Pulsar, Touchable } from 'gestalt';
import { follow, unfollow } from 'app/common/redux/actions/user/follow';

type UserFollowButtonProps = {|
  button?: ({ followText: string, onClick: () => void }) => Element<*>,
  clearMostRecentPin: () => void,
  follow: (id: string, pin_id?: ?string) => void,
  followerCount: ?number,
  fullName: string,
  hideFollowingText?: boolean,
  id: string,
  imageUrl: string,
  inline?: boolean,
  isOwnProfile: boolean,
  hideAllToasts: () => void,
  onUserFollow?: (newFollowState: boolean) => any,
  mostRecentPin: ?string,
  profileUrl: string,
  showToast: (Toast: Object) => void,
  size?: 'sm' | 'md' | 'lg',
  showFollowerCount?: boolean,
  showPulsar: boolean,
  suppressToasts?: boolean,
  unfollow: (id: string, pin_id?: ?string) => void,
  userFollowed: ?boolean,
|};

export class UserFollowButton extends PureComponent<UserFollowButtonProps> {
  handleClick = () => {
    if (this.props.userFollowed) {
      this.unfollow();
    } else {
      this.follow();
    }
  };

  handlePulsarClick = () => {
    this.handleClick();
  };

  follow: () => void = () => {
    const { follow, id, onUserFollow, mostRecentPin, clearMostRecentPin } = this.props;
    follow(id, mostRecentPin);
    this.showConfirmation();
    if (onUserFollow) {
      onUserFollow(true);
    }
    // After taking this action we don't want most recent pin to persist in case another
    // follow/unfollow event is triggered by a different set of events.
    clearMostRecentPin();
  };

  handleToastClick: () => void = () => {
    const { isOwnProfile } = this.props;
    startTiming(
      isOwnProfile
        ? PCONST.thrift.TracingClientTypes.PWTAction.OWN_PROFILE
        : PCONST.thrift.TracingClientTypes.PWTAction.OTHER_PROFILE,
    );
  };

  showConfirmation: () => void = () => {
    const { hideAllToasts, fullName, imageUrl, profileUrl, showToast, suppressToasts } = this.props;

    if (suppressToasts) {
      return;
    }

    hideAllToasts();
    showToast(({ onHide }) => (
      <Toast
        onClick={this.handleToastClick}
        onHide={onHide}
        text={[
          i18n._('Now following', 'text that comes before a board/user name in toast'),
          fullName,
        ]}
        href={profileUrl}
        imageUrl={imageUrl}
      />
    ));
  };

  unfollow: () => void = () => {
    const { id, onUserFollow, unfollow, mostRecentPin, clearMostRecentPin } = this.props;
    unfollow(id, mostRecentPin);
    if (onUserFollow) {
      onUserFollow(false);
    }
    // After taking this action we don't want most recent pin to persist in case another
    // follow/unfollow event is triggered by a different set of events.
    clearMostRecentPin();
  };

  renderButton() {
    const {
      button,
      followerCount,
      inline = false,
      size,
      showFollowerCount,
      userFollowed,
    } = this.props;

    const followText = userFollowed
      ? i18n._('Following', 'User follow button')
      : i18n._('Follow', 'User follow button');

    if (button) {
      return button({ followText, onClick: this.handleClick });
    } else if (showFollowerCount) {
      const countText =
        typeof followerCount === 'number' ? (
          <FollowersCountText
            followerCount={followerCount}
            userFollowed={userFollowed}
            followText={followText}
          />
        ) : (
          followText
        );

      return (
        <Touchable onTouch={this.handleClick}>
          <Box
            color={userFollowed ? 'lightGray' : 'red'}
            dangerouslySetInlineStyle={{ __style: { borderRadius: '8px' } }}
            paddingX={3}
            paddingY={3}
          >
            <Box display="flex" alignItems="center">
              {countText}
            </Box>
          </Box>
        </Touchable>
      );
    } else {
      return (
        <Button
          color={userFollowed ? 'gray' : 'red'}
          inline={inline}
          onClick={this.handleClick}
          size={size}
          text={followText}
        />
      );
    }
  }

  render() {
    const { showPulsar, userFollowed } = this.props;

    return (
      <Box position="relative" display="flex">
        {this.renderButton()}
        {showPulsar && (
          <Touchable onTouch={this.handlePulsarClick}>
            <Box
              position="absolute"
              display="inlineBlock"
              dangerouslySetInlineStyle={{
                __style: {
                  marginLeft: -86,
                  marginTop: -28,
                },
              }}
            >
              <Pulsar paused={userFollowed || false} />
            </Box>
          </Touchable>
        )}
      </Box>
    );
  }
}

const mapStateToProps = (state, { id, userFollowed }) => {
  const user = (state.users || {})[id];
  const { mostRecentPin } = state;
  return {
    followerCount: user ? user.follower_count : null,
    // this fallback shouldn't be necessary once all our state is in redux
    userFollowed: user ? user.explicitly_followed_by_me : userFollowed,
    mostRecentPin: mostRecentPin ? mostRecentPin.id : null,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  follow: (id, pin_id) => dispatch(follow(id, pin_id)),
  unfollow: (id, pin_id) => dispatch(unfollow(id, pin_id)),
  clearMostRecentPin: () => dispatch(setMostRecentPin(null)),
});

export default compose(
  // $FlowFixMe: add connect<Props, OwnProps, , , _, _>() typing to remove this error
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withShowToast,
)(UserFollowButton);
